<script setup lang="ts">
import { ref } from 'vue'
import { Swiper as SwiperVue, SwiperSlide } from 'swiper/vue'
import type { Swiper } from 'swiper/types' // import 'swiper/css'
import { baseUrl, padWithZeros } from '@/core/utils'
import Button from '@/core/components/base/Button.vue'
import { useListOverview } from './ListOverview'
import { usePointerSwipe, watchImmediate } from '@vueuse/core'
import { getDataOverviewSlides } from '../../helpers/getDataOverviewSlides'
import { useLanguage } from '@/core/composables/useLanguage'
import LanguageView from '@/core/components/LanguageView.vue'
//------------------------------------------------
const { currentLang } = useLanguage()
//------------------------------------------------
const swiperRef = ref<Swiper>()
const { slideIndex, hasHighlight, doUpdateSlide, doSelectPage } = useListOverview(swiperRef)

function handleSlideChange(swiper: Swiper) {
  doUpdateSlide(swiper.activeIndex)
}

//------------------------------------------------
const el = ref(null)
const { isSwiping, direction } = usePointerSwipe(el, { disableTextSelect: true })

function handleSelectPage(page: number, event: PointerEvent) {
  if (isSwiping.value) return
  doSelectPage(page)
}

//------------------------------------------------
function imageHighlightTH(page: number) {
  const langPages = [1, 2, 3, 4, 61, 68, 69]
  let langPath = langPages.includes(page) ? 'th/' : ''
  return baseUrl(`/images/index-highlight/${langPath + padWithZeros(page, 2)}.png`)
}

function imageHighlightEN(page: number) {
  const langPages = [1, 2, 3, 4, 61, 68, 69]
  let langPath = langPages.includes(page) ? 'en/' : ''
  return baseUrl(`/images/index-highlight/${langPath + padWithZeros(page, 2)}.png`)
}

watchImmediate(slideIndex, (index) => {
  swiperRef.value?.slideTo(index)
})
</script>

<template>
  <div ref="el" class="list-overview">
    <swiper-vue
      class="slideshow"
      :resistance-ratio="0"
      :direction="'vertical'"
      :slides-per-view="'auto'"
      :space-between="0"
      @swiper="(swiper) => (swiperRef = swiper)"
      @slide-change="handleSlideChange"
    >
      <template v-for="item in getDataOverviewSlides()">
        <swiper-slide class="slide">
          <!-- Item Left -->
          <div :class="['item', 'item-left', { disabled: !item.pageA }]">
            <template v-if="item.pageA">
              <img
                class="page"
                :src="baseUrl(`/images/index-pages/${currentLang}/${padWithZeros(item.pageA, 2)}.png`)"
              />
              <LanguageView lang="th">
                <img class="page-highlight blink" v-show="hasHighlight(item.pageA)" :src="imageHighlightTH(item.pageA)" />
              </LanguageView>
              <LanguageView lang="en">
                <img class="page-highlight blink" v-show="hasHighlight(item.pageA)" :src="imageHighlightEN(item.pageA)" />
              </LanguageView>
              <Button class="page-action" @tap="handleSelectPage(item.pageA, $event)" />
            </template>
          </div>

          <!-- Item Right -->
          <div :class="['item', 'item-right', { disabled: !item.pageB }]">
            <template v-if="item.pageB">
              <img
                class="page"
                :src="baseUrl(`/images/index-pages/${currentLang}/${padWithZeros(item.pageB, 2)}.png`)"
              />
              <LanguageView lang="th">
                <img class="page-highlight blink" v-show="hasHighlight(item.pageB)" :src="imageHighlightTH(item.pageB)" />
              </LanguageView>
              <LanguageView lang="en">
                <img class="page-highlight blink" v-show="hasHighlight(item.pageB)" :src="imageHighlightEN(item.pageB)" />
              </LanguageView>
              <Button class="page-action" @tap="handleSelectPage(item.pageB, $event)" />
            </template>
          </div>
        </swiper-slide>
      </template>
    </swiper-vue>
  </div>
</template>

<style scoped>
/* ---------------------------- */
.blink1 {
  animation: blink 2s linear infinite;
}

@keyframes blink {
  0%,
  100% {
    opacity: 0.3;
  }

  50%,
  70% {
    opacity: 1;
  }
}

/* ---------------------------- */
.blink {
  animation: blink 2s ease-out infinite;
}

@keyframes blink {
  0%,
  75%,
  100% {
    opacity: 0;
  }

  25%,
  50% {
    opacity: 1;
  }
}

/* ---------------------------- */
/* .blink {
  animation: blink 2s linear infinite;
}

@keyframes blink {
  0%,
  75%,
  100% {
    display: none;
  }

  25%,
  50% {
    display: block;
  }
} */
/* ---------------------------- */
.list-overview {
  position: absolute;
  width: 1350px;
  height: 1080px;
}

.slideshow {
  position: relative;
  width: 100%;
  height: 100%; /** fixed height for vertical slides */
  overflow: hidden;
}

.slide {
  width: 100%;
  height: 331px; /** fixed height for vertical slides */
  /* border: 1px dashed #f00; */
}

.item {
  position: absolute;
  background: url(/images/simakatra/bg-bar.png) repeat-y;
  width: 570px;
  height: 331px;
}

.item.disabled {
  pointer-events: none;
  cursor: auto;
}

.item-left {
  left: 105px;
  .page-highlight {
    left: 29px;
  }
}

.item-right {
  left: 730px;
  img {
    right: 0;
  }
}

.page {
  position: absolute;
}

.page-highlight {
  position: absolute;
  top: -18px;
  left: 31px;
  z-index: 200;
  /* border: 1px solid #f00; */
}

.page-action {
  position: absolute;
  left: 0px;
  width: 520px;
  height: 331px;
  z-index: 300;
  /* border: 1px dashed red; */
}

.item-right .page-action {
  left: 50px;
}

</style>
